/** @prettier */

import {useState, useEffect} from 'react';
import {alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import {utcToZonedTime} from 'date-fns-tz';
import {useTranslation} from 'react-i18next';
//interface import
import * as apiIF from '../../Type/api';

import {convertByteIntoKiloByte} from '../../functions/convertNumber';

/**  ソート方法 */
type OrderType = 'asc' | 'desc';
/** 日報データファイル情報 */
interface ReportDataInfo {
  fileName: string;
  size: string;
  timestamp: string;
}
/**
 * テーブルのヘッダー定義
 */
interface HeadCell {
  disablePadding: boolean; // パディング(true:パディングなし, false:パディングあり)
  id: keyof ReportDataInfo; // id
  label: string; // 表示するラベル
  numeric: boolean; // 数値(true:右寄せ, false:左寄せ)
}
/**
 * テーブル上部（ヘッダーの上）の引数のインターフェース
 */
interface EnhancedTableToolbarProps {
  numSelected: number; //選択数
  selectedSize: readonly string[]; //選択したファイルのサイズ一覧
  setSize: React.Dispatch<React.SetStateAction<number>>; //ファイル容量
}
/**
 * テーブルのヘッダーの引数のインターフェース
 */
interface EnhancedTableHeadProps {
  numSelected: number; // 選択数
  onRequestSort: (
    event: React.MouseEvent<unknown>, //マウスイベント
    property: keyof ReportDataInfo //プロパティ
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: OrderType; // ソート方法
  orderBy: string; // ソート順
  rowCount: number; // データ数
  id?: string; // id
}
/**
 * テーブルのボディの引数のインターフェース
 */
interface EnhancedTableBodyProps {
  rows: ReportDataInfo[];
  selectedName: readonly string[];
  timeZone: string;
  order: OrderType;
  orderBy: keyof ReportDataInfo;
  page: number;
  rowsPerPage: number;
  handleClick: (
    fileName: string,
    fileSize: string
  ) => React.MouseEventHandler<HTMLTableRowElement>;
  id?: string;
}

/**
 * 日報データファイル情報の集約化
 * @param {string} fileName  ファイル名
 * @param {string} size      ファイルサイズ
 * @param {string} timestamp タイムスタンプ
 * @returns APIから受け取った日報データファイルの情報を変換して返す
 */
const createData = (
  fileName: string,
  size: string,
  timestamp: string
): ReportDataInfo => {
  return {
    fileName: fileName,
    size: size,
    timestamp: timestamp,
  };
};

/**
 * テーブル上部（ヘッダーの上）の表示
 * @param props props
 * @returns JSX
 */
const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const trans = useTranslation().t; //翻訳関数
  const {numSelected, selectedSize, setSize} = props;
  const [totalSize, setTotalSize] = useState(0); //選択したファイルの合計サイズ

  useEffect(() => {
    const sizeSum = selectedSize.reduce((result, size) => {
      result += Number(size);
      return result;
    }, 0);
    setTotalSize(sizeSum);
    setSize(sizeSum);
  }, [numSelected, selectedSize, setSize]);

  return (
    <Toolbar
      sx={{
        ...(numSelected > 0 && {
          bgcolor: theme =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
      className="serviceInfo__dailyReportData__report__toolbar">
      <Typography
        className="serviceInfo__dailyReportData__report__toolbar__text"
        color="inherit"
        variant="subtitle1"
        component="div">
        {trans('DataTable.selectedSize', {
          number: numSelected,
          size: convertByteIntoKiloByte(totalSize, 1),
        })}
      </Typography>
    </Toolbar>
  );
};

/**
 * テーブルのヘッダーの表示
 * @param props props
 * @returns JSX
 */
const EnhancedTableHead = (props: EnhancedTableHeadProps) => {
  const {
    numSelected,
    onRequestSort,
    onSelectAllClick,
    order,
    orderBy,
    rowCount,
  } = props;
  const trans = useTranslation().t; //翻訳関数

  /** ヘッダーの設定 */
  const headCells: readonly HeadCell[] = [
    {
      id: 'fileName',
      numeric: false,
      disablePadding: true,
      label: trans('DataTable.header.fileName'),
    },
    {
      id: 'size',
      numeric: true,
      disablePadding: false,
      label: trans('DataTable.header.size'),
    },
    {
      id: 'timestamp',
      numeric: true,
      disablePadding: false,
      label: trans('DataTable.header.timeStamp'),
    },
  ];

  /**
   * ラベルを押下した時の処理
   * @param property 押下したセルのID
   * @returns
   */
  const createSortHandler = (property: keyof ReportDataInfo) => {
    return (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all desserts',
            }}
            id={props.id + 'AllSelect'}
          />
        </TableCell>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              id={props.id + headCell.id + 'SortBtn'}>
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span">{order === 'desc' ? '' : ''}</Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

/**
 * テーブルのボディの表示
 * @param props props
 * @returns JSX
 */
const EnhancedTableBody = (props: EnhancedTableBodyProps) => {
  // このメソッドは、ブラウザー間の互換性のために作成されています。IE11 をサポートする必要がない場合は、Array.prototype.sort() を直接使用できます。
  const trans = useTranslation().t; //翻訳関数

  /**
   *　ソート関数
   * @param {T[]} array ソート対象のデータ
   * @param {(a: T, b: T) => number} comparator 順序付け関数
   * @returns　stabilizedThis　ソートした配列
   */
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
  }
  /**
   * (Generics) string | number
   * データAとデータBの大小判定
   * @param {T} a　データA
   * @param {T} b　データB
   * @param {T} orderBy キー（日報データファイル名 {React.useState<keyof reportDataInfo>('Filename')}）
   * @returns -1 | 0 | 1
   */
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
  /**
   * 順序付け関数
   * @param {OrderType} order ソート方法
   * @param {Key} orderBy キー(File Name|Size(KB)|Time stamp)
   * @returns　a,bの順序付け(-1|0|1)
   */
  function getComparator<Key extends keyof any>(
    order: OrderType,
    orderBy: Key
  ): (
    a: {[key in Key]: number | string},
    b: {[key in Key]: number | string}
  ) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  //空の行がある最後のページに到達したときのレイアウト ジャンプを回避します。
  const emptyRows = (page: number, rowsPerPage: number, rowsSize: number) => {
    return page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowsSize) : 0;
  };

  return (
    <TableBody>
      {stableSort(props.rows, getComparator(props.order, props.orderBy))
        .slice(
          props.page * props.rowsPerPage,
          props.page * props.rowsPerPage + props.rowsPerPage
        )
        .map((row, index) => {
          const isItemSelected = props.selectedName.includes(row.fileName);
          const labelId = `enhanced-table-checkbox-${index}`;
          /** タイムスタンプ */
          const timestamp =
            row.timestamp === ''
              ? ''
              : utcToZonedTime(new Date(row.timestamp), props.timeZone);
          return (
            <TableRow
              hover
              onClick={props.handleClick(row.fileName, row.size)}
              role="checkbox"
              aria-checked={isItemSelected}
              tabIndex={-1}
              key={row.fileName}
              selected={isItemSelected}
              id={props.id + row.fileName}>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={isItemSelected}
                  inputProps={{
                    'aria-labelledby': labelId,
                  }}
                />
              </TableCell>
              <TableCell component="th" id={labelId} scope="row" padding="none">
                {row.fileName}
              </TableCell>
              <TableCell align="right">
                {convertByteIntoKiloByte(Number(row.size), 1)}
              </TableCell>
              <TableCell align="right">
                {timestamp === ''
                  ? ''
                  : //prettier-ignore
                    `${trans('DataTable.table.timeStamp', {
                      year: timestamp.getFullYear(),
                      month: String(timestamp.getMonth() + 1).padStart(2, '0'),
                      date: String(timestamp.getDate()).padStart(2, '0'),
                      hour: String(timestamp.getHours()).padStart(2, '0'),
                      minutes: String(timestamp.getMinutes()).padStart(2, '0'),
                    })}`}
              </TableCell>
            </TableRow>
          );
        })}
      {emptyRows(props.page, props.rowsPerPage, props.rows.length) > 0 && (
        <TableRow
          style={{
            height:
              53 * emptyRows(props.page, props.rowsPerPage, props.rows.length),
          }}>
          <TableCell colSpan={6} />
        </TableRow>
      )}
    </TableBody>
  );
};

/**
 * レポートデータテーブル
 * @param props props
 * @returns JSX
 * - tableData APIから取得したレポートデータ一覧
 * - tableClassName データテーブルに当てるCSSクラス
 * - setRetData 選択したレポートデータのリスト更新関数
 * - setSize　選択したレポートデータのファイルサイズ更新関数
 * - timeZone タイムゾーン
 * - id id
 */
const EnhancedTable = (props: {
  tableData: apiIF.resParameters.report[];
  tableClassName: string;
  setRetData: React.Dispatch<React.SetStateAction<readonly string[]>>;
  setSize: React.Dispatch<React.SetStateAction<number>>;
  timeZone: string;
  id?: string;
  orderBy: keyof ReportDataInfo;
}) => {
  const [order, setOrder] = useState<OrderType>('desc'); // ソート方法
  const [orderBy, setOrderBy] = useState<keyof ReportDataInfo>(props.orderBy); // ソート対象
  const [selectedName, setSelectedName] = useState<readonly string[]>([]); // 選択したファイル名一覧
  const [selectedSize, setSelectedSize] = useState<readonly string[]>([]); // 選択したファイルのサイズ一覧
  const [page, setPage] = useState(0); // ページ番号
  const [rowsPerPage, setRowsPerPage] = useState(25); //1ページあたりの項目数
  const [rows, setRows] = useState<ReportDataInfo[]>([createData('', '', '')]); // 1行分データ

  useEffect(() => {
    setRows(
      props.tableData.map(data => {
        return createData(
          data['file-name'],
          data['file-size'],
          data['time-stamp']
        );
      })
    );
  }, [props.tableData]);

  /**
   * ファイル一括選択時処理
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns
   */
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newAllSelectedName = rows.map(n => n.fileName);
      setSelectedName(newAllSelectedName);
      props.setRetData(newAllSelectedName);
      const newAllSelectedSize = rows.map(n => n.size);
      setSelectedSize(newAllSelectedSize);
      return;
    }
    setSelectedName([]);
    setSelectedSize([]);
    props.setRetData([]);
  };
  /**
   * ソート時処理
   * @param {React.MouseEvent<unknown>} event
   * @param {keyof ReportDataInfo} property
   */
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof ReportDataInfo
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  /**
   * データ選択時処理
   * @param {React.MouseEvent<unknown>} event マウスイベント
   * @param {string} name ファイル名
   * @param {string} size ファイルサイズ
   */
  const handleClick = (
    event: React.MouseEvent<unknown>,
    name: string,
    size: string
  ) => {
    /** 同じファイルサイズが複数ある場合,indexofの検索が上手くいかないため */
    /** sizeでなく、ユニークであるnameで検索 */
    const selectedIndex = selectedName.indexOf(name);

    let newSelectedItemName: readonly string[] = [];
    let newSelectedItemSize: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelectedItemName = newSelectedItemName.concat(selectedName, name);
      newSelectedItemSize = newSelectedItemSize.concat(selectedSize, size);
    } else if (selectedIndex === 0) {
      newSelectedItemName = newSelectedItemName.concat(selectedName.slice(1));
      newSelectedItemSize = newSelectedItemSize.concat(selectedSize.slice(1));
    } else if (selectedIndex === selectedName.length - 1) {
      newSelectedItemName = newSelectedItemName.concat(
        selectedName.slice(0, -1)
      );
      newSelectedItemSize = newSelectedItemSize.concat(
        selectedSize.slice(0, -1)
      );
    } else if (selectedIndex > 0) {
      newSelectedItemName = newSelectedItemName.concat(
        selectedName.slice(0, selectedIndex),
        selectedName.slice(selectedIndex + 1)
      );
      newSelectedItemSize = newSelectedItemSize.concat(
        selectedSize.slice(0, selectedIndex),
        selectedSize.slice(selectedIndex + 1)
      );
    }
    setSelectedName(newSelectedItemName);
    props.setRetData(newSelectedItemName);
    setSelectedSize(newSelectedItemSize);
  };
  /**
   * 表示ページ変更時処理
   * @param {unknown} event イベント
   * @param {number} newPage ページ番号
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  /**
   * 1ページ当たりの項目数変更時処理
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper className="serviceInfo__dailyReportData__report serviceInfo__dailyReportData__report999__list">
      <EnhancedTableToolbar
        numSelected={selectedName.length}
        selectedSize={selectedSize}
        setSize={props.setSize}
      />
      <TableContainer id={`${props.id}Table`} className={props.tableClassName}>
        <Table stickyHeader aria-labelledby="tableTitle" size="medium">
          <EnhancedTableHead
            numSelected={selectedName.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            id={props.id}
          />
          <EnhancedTableBody
            rows={rows}
            selectedName={selectedName}
            timeZone={props.timeZone}
            order={order}
            orderBy={orderBy}
            page={page}
            rowsPerPage={rowsPerPage}
            handleClick={(fileName, fileSize) => {
              return event => handleClick(event, fileName, fileSize);
            }}
            id={props.id}
          />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        id={`${props.id}Pagination`}
      />
    </Paper>
  );
};
export default EnhancedTable;
